import { useState, useEffect } from 'react';
import {
  getTreatmentSupportRequests,
  addTreatmentSupportRequest,
  updateTreatmentSupportRequest,
  deleteTreatmentSupportRequest
} from '@/lib/firebase/queries/treatmentSupport';
import type { TreatmentSupportRequest } from '@/types';

export function useTreatmentSupportRequests() {
  const [requests, setRequests] = useState<TreatmentSupportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTreatmentSupportRequests();
      setRequests(data);
    } catch (err) {
      console.error('Failed to fetch treatment support requests:', err);
      setError('Failed to fetch treatment support requests');
    } finally {
      setLoading(false);
    }
  };

  const addRequest = async (request: Omit<TreatmentSupportRequest, 'id'>): Promise<number> => {
    try {
      const caseNumber = await addTreatmentSupportRequest(request);
      await fetchRequests();
      return caseNumber;
    } catch (err) {
      console.error('Failed to add treatment support request:', err);
      throw err;
    }
  };

  const updateRequest = async (id: string, data: Partial<TreatmentSupportRequest>) => {
    try {
      await updateTreatmentSupportRequest(id, data);
      await fetchRequests();
    } catch (err) {
      console.error('Failed to update treatment support request:', err);
      throw err;
    }
  };

  const deleteRequest = async (id: string) => {
    try {
      await deleteTreatmentSupportRequest(id);
      await fetchRequests();
    } catch (err) {
      console.error('Failed to delete treatment support request:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  return {
    requests,
    loading,
    error,
    refreshRequests: fetchRequests,
    addRequest,
    updateRequest,
    deleteRequest
  };
} 
